import _ from "lodash";
import React, { useCallback } from "react";
import classNames from "classnames";
import { Tag, Tooltip, IconButton } from "@toddle-edu/ds-web";
import {
  PencilLineOutlined,
  ReloadArrowOutlined,
  ReSubmissionRequestOutlined,
  WarningCircleColored,
} from "@toddle-edu/ds-icons";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getIsEnabledExcusedStatusInAssignment } from "Utils/ACLStoreUtils";
import { useI18n } from "Hooks";
import classes from "./StatusIcon.scss";

const StatusIcon = props => {
  const {
    showStatusIcon = false,
    statusTooltip = "",
    toolTipStyle = {},
    statusIcon,
    tooltipPlacement = "bottom",
    alignConfig,
    statusTagState,
    tagLabelStyle = {},
    tagWrapperStyle = {},
    tagContainerStyle = {},
    naContainerStyle = {},
    overrideConfig = null,
    isResubmission = false,
    resubmissionTooltip = "",
    centerTag = false,
  } = props;

  const { t } = useI18n();

  const {
    onOverrideClick = null,
    onResetOverride = null,
    systemStatus = null,
    statusV2 = null,
    isStudentUpdatedOverriddenStatus = false,
  } = overrideConfig || {};

  const { showTag, tagColor, tagIcon, tagLabel, tagTooltip } = statusTagState;

  const handlePencilClick = useCallback(
    e => {
      e.stopPropagation();
      if (onOverrideClick) onOverrideClick();
    },
    [onOverrideClick]
  );

  const handleResetOverrideClick = useCallback(
    e => {
      if (e) e.stopPropagation();
      if (onResetOverride) onResetOverride();
    },
    [onResetOverride]
  );

  const renderPencilSuffix = () => {
    if (!onOverrideClick) return null;
    return (
      <Tooltip
        tooltip={t("classRoom:change_submission_status")}
        placement="bottom"
        showArrow={true}
        dsVersion="2.0"
      >
        <IconButton
          type="neutral"
          size="x-small"
          variant="subtle"
          icon={<PencilLineOutlined variant="subtle" size="xxx-small" />}
          onClick={handlePencilClick}
          dsVersion="2.0"
          testId="status-icon-change-status-button"
        />
      </Tooltip>
    );
  };

  const renderOverrideIndicator = () => {
    if (isStudentUpdatedOverriddenStatus) {
      return (
        <Tooltip
          dsVersion="2.0"
          tooltip={t("classRoom:student_updated_overridden_status")}
          showArrow={true}
          placement="bottom"
        >
          <WarningCircleColored size="xxx-small" />
        </Tooltip>
      );
    }
    if (!_.isNil(systemStatus) && statusV2 !== systemStatus) {
      return (
        <Tooltip
          dsVersion="2.0"
          showArrow={true}
          tooltip={
            <div className={classes.overrideTooltipContent}>
              <span>{t("classRoom:status_overridden_tooltip")}</span>
              <span className={classes.resetLink}>
                {t("classRoom:click_to_reset")}
              </span>
            </div>
          }
          placement="bottom"
        >
          <IconButton
            dsVersion="2.0"
            type="neutral"
            size="x-small"
            variant="subtle"
            icon={<ReloadArrowOutlined variant="subtle" size="xxx-small" />}
            onClick={handleResetOverrideClick}
            aria-label={t("classRoom:click_to_reset")}
            testId="status-icon-reset-override-button"
          />
        </Tooltip>
      );
    }
    return null;
  };

  const renderStatusSuffix = () => {
    const pencilSuffix = renderPencilSuffix();
    const overrideIndicator = renderOverrideIndicator();
    if (!pencilSuffix && !overrideIndicator && !isResubmission) return null;
    return (
      <div className={classes.statusSuffixSlot}>
        {isResubmission ? (
          <Tooltip
            dsVersion="2.0"
            showArrow={true}
            tooltip={resubmissionTooltip || t("classRoom:request_resubmission")}
            placement="bottom"
          >
            <ReSubmissionRequestOutlined variant="subtle" size="xxx-small" />
          </Tooltip>
        ) : null}
        {overrideIndicator}
        {pencilSuffix ? (
          <span className={classes.pencilIcon}>{pencilSuffix}</span>
        ) : null}
      </div>
    );
  };

  const statusSuffix = renderStatusSuffix();

  const renderTag = () => {
    switch (tagColor) {
      // custom tag like component
      case "transparent":
        return (
          <>
            <ConditionalWrapper
              type="wrapperAsElement"
              condition={!_.isEmpty(tagTooltip)}
              wrapper={
                <Tooltip
                  tooltip={tagTooltip}
                  showArrow={true}
                  placement="bottom"
                />
              }
            >
              <div
                className={classes.tagContainer}
                style={{ backgroundColor: tagColor, ...tagContainerStyle }}
              >
                {tagIcon ? (
                  <div className={classes.tagIcon}>{tagIcon}</div>
                ) : null}
                <div className={classes.tagLabel} style={tagLabelStyle}>
                  {tagLabel}
                </div>
              </div>
            </ConditionalWrapper>
            {statusSuffix}
          </>
        );
      default:
        return (
          <div
            className={classNames(classes.tagWrapper, {
              [classes.tagWrapperClickable]: !!statusSuffix,
              [classes.tagWrapperCentered]: centerTag && !!statusSuffix,
            })}
            style={tagWrapperStyle}
          >
            <Tag
              dsVersion="2.0"
              size="small"
              color={tagColor}
              prefix={tagIcon}
              shouldTruncate={false}
            >
              <ConditionalWrapper
                type="wrapperAsElement"
                condition={!_.isEmpty(tagTooltip)}
                wrapper={
                  <Tooltip
                    tooltip={tagTooltip}
                    showArrow={true}
                    placement="bottom"
                  />
                }
              >
                <div
                  className={classes.submissionStatusLabel}
                  style={tagLabelStyle}
                >
                  {tagLabel}
                </div>
              </ConditionalWrapper>
            </Tag>
            {statusSuffix}
          </div>
        );
    }
  };

  return showStatusIcon ? (
    <>
      {statusIcon ? (
        <LinkWithTooltip
          placement={tooltipPlacement}
          tooltip={statusTooltip}
          mouseEnterDelay={0.5}
          tooltipContainerStyle={toolTipStyle}
          alignConfig={alignConfig}
        >
          <div className={classes.iconCon}>{statusIcon}</div>
        </LinkWithTooltip>
      ) : null}
      {showTag ? renderTag() : null}
    </>
  ) : (
    <div
      className={classNames("text-body-s", classes.naContainer)}
      style={naContainerStyle}
    >
      {getIsEnabledExcusedStatusInAssignment() ? t("classRoom:excused") : "-"}
    </div>
  );
};

export default StatusIcon;
